import type { Lot } from '../types/lot'
import { lotApi } from './lotApi'
import { fishStockLogApi } from './fishStockLogApi'
import { weightSampleLogApi } from './weightSampleLogApi'
import { upsertProductionUnitTableRowAndSync, type BiomassDataRow } from './productionUnitTable'
import { createId } from './id'
import { withEggArrival, type EggArrival } from './hatchery'
import { withLotCapacityAssignment, type LotCapacityAssignment } from './carryingCapacity'
import { buildBiomassFishStockMetadata } from '../screens/biomass/fishStockLogBridge'
import { buildBiomassSamplingWeightMetadata } from '../screens/biomass/weightSampleLogBridge'

// v2.2 findings #67 / #71 — "Sembrar en varias unidades".
//
// 1. One server transaction creates the arrival lot (facility level) and one
//    same-name child lot per chosen unit, sharing lineage
//    (`POST /lots/stock-into-units`).
// 2. For each child, the stocking day is recorded exactly like a Muestreo
//    "Conteo": a production row for that day plus a FishStockLog (method
//    'count') and, when an arrival weight is given, a WeightSampleLog — so the
//    stock and weight show in Base de datos, Reportes and Panel from day one.
//    These writes go through the offline outbox like every other capture.

export type StockIntoUnitsTarget = {
  productionUnitId: string
  productionUnitName?: string
  /** Fish — or eggs for an egg batch — stocked in this unit. */
  fishCount?: number
  /** This unit's arrival weight (findings #106); falls back to the batch weight. */
  averageWeightG?: number
}

export type StockIntoUnitsArgs = {
  farmId: string
  farmName?: string
  lotKey: string
  species?: string
  /** ISO date-time of the stocking day (calendar-day sentinel is fine). */
  stockingDate: string
  averageWeightG?: number
  notes?: string
  /** Findings #78: seed supplier (hatchery / origin) and strain — ASC 1.5.2, BAP T4. */
  seedSupplier?: string
  strain?: string
  units: StockIntoUnitsTarget[]
  /** Egg batch (findings #109): the shared arrival record; each unit's count becomes its eggs received. */
  eggArrival?: { arrival: EggArrival; supplier?: string; strain?: string }
  /** Capacidad de carga (findings #151): the copy of the chosen table, kept on every unit lot (the unit-less arrival record is never updated). */
  capacity?: LotCapacityAssignment
}

export type StockIntoUnitsResult = {
  parentLot: Lot
  childLots: Lot[]
  /** Findings #152: settles once the per-unit arrival / table copies (queued in the outbox) have been sent. */
  pendingSync?: Promise<unknown>
}

export const stockLotIntoUnits = async (args: StockIntoUnitsArgs): Promise<StockIntoUnitsResult> => {
  const lotKey = args.lotKey.trim()
  const withCounts = args.units.every((unit) => typeof unit.fishCount === 'number' && unit.fishCount > 0)

  const { parentLot, childLots } = await lotApi.stockIntoUnitsRemote({
    farmId: args.farmId,
    lotKey,
    species: args.species?.trim() || undefined,
    stockingDate: args.stockingDate,
    notes: args.notes?.trim() || undefined,
    seedSupplier: args.seedSupplier?.trim() || undefined,
    strain: args.strain?.trim() || undefined,
    children: args.units.map((unit) => ({
      productionUnitId: unit.productionUnitId,
      ...(withCounts ? { fishCount: unit.fishCount } : {})
    }))
  })

  const syncPromises: Array<Promise<unknown>> = []
  for (const [index, child] of childLots.entries()) {
    const unit = args.units[index]
    await recordStockingDay({
      farmId: args.farmId,
      farmName: args.farmName,
      productionUnitId: unit.productionUnitId,
      productionUnitName: unit.productionUnitName,
      lotId: child.id,
      lotKey,
      date: args.stockingDate,
      count: withCounts ? unit.fishCount : undefined,
      // findings #106: a unit's own arrival weight wins over the batch weight.
      averageWeightG: unit.averageWeightG ?? args.averageWeightG,
      notes: args.notes
    })
    if (args.eggArrival || args.capacity) {
      // findings #109: every incubator gets the batch's arrival record with its own egg count.
      const withArrival = args.eggArrival
        ? withEggArrival(child, {
          ...args.eggArrival,
          arrival: { ...args.eggArrival.arrival, ...(unit.fishCount ? { eggsReceived: unit.fishCount } : {}) },
          arrivalDate: args.stockingDate.slice(0, 10),
          recordedAt: new Date().toISOString()
        })
        : child
      const updated = await lotApi.update(child.id, {
        metadata: args.capacity ? withLotCapacityAssignment(withArrival, args.capacity).metadata : withArrival.metadata
      })
      if (updated.syncPromise) syncPromises.push(updated.syncPromise)
    }
  }

  return {
    parentLot,
    childLots,
    ...(syncPromises.length > 0 ? { pendingSync: Promise.allSettled(syncPromises) } : {})
  }
}

/** Even split of a total across N units; the remainder goes to the first units. */
export const splitFishEvenly = (total: number, unitCount: number): number[] => {
  if (!Number.isFinite(total) || total <= 0 || unitCount <= 0) return []
  const whole = Math.floor(total)
  const base = Math.floor(whole / unitCount)
  const remainder = whole - base * unitCount
  return Array.from({ length: unitCount }, (_, index) => base + (index < remainder ? 1 : 0))
}

/**
 * The stocking day of one lot in one unit, recorded exactly like a Muestreo
 * "Conteo": a production row plus a FishStockLog (method 'count') and, with a
 * weight, a WeightSampleLog — so the stock shows in Base de datos, Biomasa and
 * the reports from day one. Also the opening stock of an egg lot: the eggs
 * received (findings #110). Goes through the offline outbox like any capture.
 */
export const recordStockingDay = async (args: {
  farmId: string
  farmName?: string
  productionUnitId: string
  productionUnitName?: string
  lotId: string
  lotKey: string
  /** ISO date-time of the stocking day (calendar-day sentinel is fine). */
  date: string
  count?: number
  averageWeightG?: number
  notes?: string
}): Promise<void> => {
  const count = typeof args.count === 'number' && args.count > 0 ? Math.round(args.count) : undefined
  const weight = typeof args.averageWeightG === 'number' && Number.isFinite(args.averageWeightG) && args.averageWeightG > 0
    ? args.averageWeightG
    : undefined
  if (!count && !weight) return

  const context = {
    farmId: args.farmId,
    productionUnitId: args.productionUnitId,
    farmName: args.farmName,
    productionUnitName: args.productionUnitName
  }
  const rowId = createId('prod')
  const row: BiomassDataRow = {
    id: rowId,
    date: args.date,
    lotId: args.lotId,
    lot: args.lotKey,
    stage: 'Active',
    weight: '',
    dailyMortality: '',
    fishStock: count ? String(count) : '',
    upVol: '',
    health: '',
    healthDetail: '',
    ...(count ? { stockMethod: 'exact' } : {})
  } as BiomassDataRow
  await upsertProductionUnitTableRowAndSync(context, row)

  if (count) {
    await fishStockLogApi.create({
      farmId: args.farmId,
      productionUnitId: args.productionUnitId,
      lot: args.lotKey,
      lotId: args.lotId,
      productionRowId: rowId,
      recordedAt: args.date,
      countedStock: count,
      method: 'count',
      notes: args.notes?.trim() || undefined,
      metadata: {
        ...buildBiomassFishStockMetadata({ productionRowId: rowId, lotId: args.lotId, stage: 'Active' }),
        stockedIntoUnits: true
      }
    })
  }

  if (weight) {
    await weightSampleLogApi.create({
      farmId: args.farmId,
      productionUnitId: args.productionUnitId,
      lot: args.lotKey,
      lotId: args.lotId,
      productionRowId: rowId,
      recordedAt: args.date,
      weightGrams: String(weight),
      metadata: {
        ...buildBiomassSamplingWeightMetadata({ productionRowId: rowId, lotId: args.lotId, stage: 'Active' }),
        stockedIntoUnits: true
      }
    })
  }
}
